import Text from "@/components/Text";
import {
  useEffect,
  useState,
  type Dispatch,
  type FC,
  type SetStateAction,
} from "react";
import type { RestaurantAutocompleteResult, RestaurantOverview } from "@/API";
import { Checkbox } from "@/components/ui/checkbox";
import { getRestaurantOverview } from "@/graphql/queries";
import { generateClient } from "aws-amplify/api";
import { Skeleton } from "@/components/ui/skeleton";
import KrunshScore from "@/components/KrunshScore";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

const client = generateClient();

type Props = {
  restaurants: RestaurantAutocompleteResult[];
  setRestaurantsSelected: Dispatch<
    SetStateAction<RestaurantAutocompleteResult[]>
  >;
  restaurantsSelected: RestaurantAutocompleteResult[];
  finish: VoidFunction;
  restart: VoidFunction;
};

const OnboardingAddRestaurantsStep3: FC<Props> = ({
  restaurants,
  setRestaurantsSelected,
  restaurantsSelected,
  finish,
  restart,
}) => {
  const { t } = useTranslation();

  const [restaurantDetails, setRestaurantDetails] =
    useState<RestaurantOverview | null>(null);
  const [restaurantToPreview, setRestaurantToPreview] = useState<string>(
    restaurants[0].id,
  );

  useEffect(() => {
    const fetchRestaurant = async (restaurantId: string) => {
      try {
        const { data } = await client.graphql({
          query: getRestaurantOverview,
          variables: {
            id: restaurantId,
          },
        });

        setRestaurantDetails(data.getRestaurantOverview as RestaurantOverview);
      } catch (e) {
        console.log("e", e);
      }
    };

    fetchRestaurant(restaurantToPreview);
  }, [restaurantToPreview]);

  return (
    <div className="flex flex-col gap-[24px]">
      {/* Step */}
      <div className="bg-[#F4F4F4] py-[4px] px-[8px] rounded-[40px] w-[fit-content]">
        <Text fontSize={14} fontWeight={500} color="#393939">
          {t("step_progress", { step: 3, end: 3 })}
        </Text>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-[10px]">
        <Text fontSize={28} fontWeight={700}>
          {t("onboarding_add_restaurants_step_3_title")}
        </Text>
        <Text fontSize={14} fontWeight={400} color="#393939">
          {t("onboarding_add_restaurants_step_3_description")}
        </Text>
      </div>

      {/* Content */}
      <div className="flex flex-col sm:flex-row w-[100%] gap-[24px]">
        {/* Restaurants */}
        <div className="flex flex-col w-[100%] gap-[8px] max-h-[280px] overflow-y-auto">
          {restaurants.map((restaurant) => (
            <div
              key={restaurant.id}
              className="p-[16px] rounded-[16px] flex items-center justify-between cursor-pointer"
              style={{ border: "1px solid #C7D1DB" }}
              onClick={() => {
                setRestaurantDetails(null);
                setRestaurantToPreview(restaurant.id);
              }}
            >
              <div className="flex gap-[12px] items-center">
                <img
                  className="h-[40px] w-[40px] min-h-[40px] min-w-[40px] rounded-[50%]"
                  src={restaurant?.imageUrl ?? undefined}
                />
                <img
                  className="h-[40px] w-[40px] min-h-[40px] min-w-[40px] rounded-[50%] ml-[-32px]"
                  src={`/partner/${restaurant.platform.toLowerCase()}.png`}
                />
                <div className="flex flex-col">
                  <Text fontSize={14} fontWeight={500} color="#393939">
                    {restaurant.name}
                  </Text>
                  <Text fontSize={12} fontWeight={500} color="#535862">
                    {restaurant.address}
                  </Text>
                </div>
              </div>

              <Checkbox
                checked={Boolean(
                  restaurantsSelected.find((r) => r.id === restaurant.id),
                )}
                onCheckedChange={(open) => {
                  if (open) {
                    setRestaurantsSelected((prevState) => [
                      ...prevState,
                      restaurant,
                    ]);
                  } else {
                    setRestaurantsSelected((prevState) =>
                      prevState.filter((r) => r.id !== restaurant.id),
                    );
                  }
                }}
              />
            </div>
          ))}
        </div>

        {/* Preview restaurant */}
        {restaurantDetails ? (
          <div
            style={{ border: "1px solid #F4F4F4" }}
            className="flex flex-col bg-[white] rounded-[16px] w-[100%] sm:min-w-[242px] sm:w-[242px] p-[8px]"
          >
            <div className="relative">
              <img
                src={restaurantDetails?.imageUrl ?? undefined}
                alt={`image of ${restaurantDetails.name}`}
                className="aspect-3/2 object-cover rounded-[16px]"
              />
              <img
                width={32}
                height={32}
                className="rounded-[50%] absolute top-[16px] left-[16px]"
                src={`/partner/${restaurantDetails.platform.toLowerCase()}.png`}
                alt={`logo of ${restaurantDetails.platform}`}
              />
            </div>

            <div className="flex items-center gap-[12px] mt-[12px]">
              <KrunshScore value={restaurantDetails.krunshScore} />
              <div className="flex flex-col max-w-[160px]">
                <Text fontSize={14} fontWeight={600} color="#44546F">
                  {restaurantDetails.name}
                </Text>
                <Text
                  fontSize={12}
                  fontWeight={400}
                  color="#535862"
                  className="truncate"
                >
                  {restaurantDetails.address}
                </Text>
              </div>
            </div>

            <div
              style={{ marginTop: 20 }}
              className="grid grid-cols-2 w-[100%] gap-[8px]"
            >
              <div
                className="flex flex-col p-[12px] rounded-[12px]"
                style={{ border: "1px solid #F4F4F4" }}
              >
                <Text fontSize={14} fontWeight={400} color="#6F6F6F">
                  {t("onboarding_add_restaurants_step_3_3")}
                </Text>
                <Text
                  fontSize={14}
                  fontWeight={400}
                  color="#393939"
                >{`${restaurantDetails.nearbyIndex + 1}e / ${restaurantDetails.nearbyRestaurants.restaurants?.length}`}</Text>
              </div>
              <div
                className="flex flex-col p-[12px] rounded-[12px]"
                style={{ border: "1px solid #F4F4F4" }}
              >
                <Text fontSize={14} fontWeight={400} color="#6F6F6F">
                  {t("onboarding_add_restaurants_step_3_4")}
                </Text>
                <Text fontSize={14} fontWeight={400} color="#393939">
                  {restaurantDetails.nearbyRestaurants.restaurants?.length}
                </Text>
              </div>
            </div>
          </div>
        ) : (
          <Skeleton className="w-[242px] min-w-[242px] h-[290px] rounded-[16px]" />
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-col-reverse sm:flex-row justify-between items-center gap-[10px] w-[100%]">
        <Button
          variant="secondaryFilled"
          onClick={restart}
          className="w-[100%] sm:w-[fit-content]"
        >
          {t("onboarding_add_restaurants_step_3_5")}
        </Button>

        <div className="flex flex-col sm:flex-row sm:items-center gap-[10px] w-[100%] sm:w-[auto]">
          <Text fontSize={14} fontWeight={400} color="#525252">
            {t("onboarding_add_restaurants_step_3_7", {
              count: restaurantsSelected.length,
            })}
          </Text>
          <Button
            disabled={restaurantsSelected.length === 0}
            onClick={finish}
            className="w-[100%] sm:w-[fit-content]"
          >
            {t("onboarding_add_restaurants_step_3_6")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingAddRestaurantsStep3;
